import { useState, useCallback } from 'react'
import { Container, Heading, HStack, VStack, Button } from '@chakra-ui/react';
import { FiUserPlus } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import {
  ContactSearchForm,
  ContactSearchResults,
  ContactSearchPagination,
} from '../components/contacts'
import { ContactCreateModal } from '../components/contacts/ContactCreateModal'
import { useContactSearch, useContactSearchOptions } from '../hooks/useContactSearch'
import type { ContactSearchRequest } from '../types/contact'

export function ContactSearchPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useState<ContactSearchRequest>({
    page: 1,
    pageSize: 25,
    useSmartNameSearch: true,
  })
  const [hasSearched, setHasSearched] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const { data: options } = useContactSearchOptions()
  const { data: results, isLoading, isFetching } = useContactSearch(searchParams, hasSearched)

  const handleSearch = useCallback((data: ContactSearchRequest) => {
    setSearchParams({
      ...data,
      page: 1,
      pageSize: searchParams.pageSize,
    })
    setHasSearched(true)
  }, [searchParams.pageSize])

  const handleClear = useCallback(() => {
    setSearchParams({
      page: 1,
      pageSize: 25,
      useSmartNameSearch: true,
    })
    setHasSearched(false)
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setSearchParams((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setSearchParams((prev) => ({ ...prev, pageSize, page: 1 }))
  }, [])

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between" wrap="wrap" gap={3}>
          <Heading size="lg">Contact search</Heading>
          <Button
            colorPalette="brand"
            onClick={() => setIsCreateOpen(true)}
            minH="44px"
          >
            <FiUserPlus />
            Add contact
          </Button>
        </HStack>

        <ContactSearchForm
          options={options}
          onSearch={handleSearch}
          onClear={handleClear}
          isLoading={isFetching}
        />

        {hasSearched && (
          <>
            <ContactSearchResults data={results} isLoading={isLoading} />
            {results && results.totalCount > 0 && (
              <ContactSearchPagination
                data={results}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </>
        )}
      </VStack>

      <ContactCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        context={{ kind: 'standalone' }}
        onCreated={(id) => navigate('/dashboard/contacts/' + id)}
      />
    </Container>
  );
}
